import { useSuspenseQueries } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { networksQueryOptions } from '#api/networks.queries.ts';
import { maxTagsQueryOptions } from '#api/tags.queries.ts';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { getCidrV4, getCidrV6 } from '#util/networkCidrs.ts';

/** {@link useSuspenseHostFormDeps} loads network and config tag details for use in the unprefixed `HostForm` */
export function useSuspenseHostFormDeps() {
  const [networkQuery, maxTagsQuery] = useSuspenseQueries({
    queries: [networksQueryOptions, maxTagsQueryOptions],
  });
  const persistedNetworkFilter = usePersistedNetworkFilter();

  const networks = networkQuery.data.data;
  const networkCount = networkQuery.data.metadata.totalCount;
  const defaultNetworkID =
    networkCount === 1 ?
      assert(networks[0], 'Expected organization to have at least one network').id
      // If there are multiple networks, default to the one being filtered
    : persistedNetworkFilter.replace('?filter.networkID=', '');

  const v4CIDRs = networks.map((n) => getCidrV4(n)).filter((cidr): cidr is string => typeof cidr === 'string');
  const v6CIDRs = networks.map((n) => getCidrV6(n)).filter((cidr): cidr is string => typeof cidr === 'string');
  const allTags = maxTagsQuery.data.data;
  const shouldShowNetworkSelect = networkCount > 1;

  return {
    /** network ID to fill DEFAULT_VALUES with, useful when there's only one network. */
    defaultNetworkID,
    networks,
    v4CIDRs,
    v6CIDRs,
    allTags,
    shouldShowNetworkSelect,
  };
}
